import React from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { object, string } from "yup";

import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Popper from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/styles";
import { Theme, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";

import FormInput from "components/FormInput";
import useTKey from "utils/useTKey";

const useStyles = makeStyles((theme: Theme) => ({
  inputDialog: ({ width }: { width: number | undefined }) => ({
    backgroundColor: theme.palette.grey[100],
    boxShadow: `2px 2px 2px 1px ${theme.palette.grey[200]}`,
    padding: "1rem",
    width: `${width}px`,
  }),
  popper: {
    "z-index": "1300",
  },
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  closeIcon: {
    padding: ".2rem",
  },
}));

const useValidationSchema = () => {
  const t = useTKey("common.error.form");
  return object({
    email: string().email(t("email")).required(t("required")),
  });
};

type Props = {
  onSubmit: (form: { email: string }) => Promise<void>;
};

const InviteAdmin: React.FC<Props> = ({ onSubmit }) => {
  const [t] = useTranslation();
  const [inputEl, setInputEl] = React.useState<HTMLElement | undefined>();
  const styles = useStyles({ width: inputEl ? inputEl.offsetWidth : undefined });
  const schema = useValidationSchema();

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    onSubmit: async ({ email }, { setFieldValue }) => {
      setInputEl(undefined);
      onSubmit({ email }).then(() => setFieldValue("email", ""));
    },
    validationSchema: schema,
    isInitialValid: false,
    validateOnMount: false,
  });

  return (
    <Grid item xs={12} sm={5}>
      <form>
        <Grid container>
          <FormInput
            name="email"
            value={formik.values.email}
            label={t("systemUsers.addUser")}
            placeholder={t("systemUsers.emailPlaceholder")}
            onFocus={(e) => setInputEl(e.currentTarget)}
            onChange={formik.handleChange}
          />
          <Popper
            open={Boolean(inputEl)}
            className={styles.popper}
            anchorEl={inputEl}
            onClick={(e) => {
              e.preventDefault();
            }}
          >
            <Grid container className={styles.inputDialog} direction="column" spacing={2} id="popper">
              <Grid item className={styles.itemContainer}>
                <Typography>{t("systemUsers.enterEmail")}</Typography>
                <IconButton
                  data-testid="icon-button"
                  onClick={() => setInputEl(undefined)}
                  color="secondary"
                  className={styles.closeIcon}
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!formik.isValid}
                  onClick={() => formik.handleSubmit()}
                >
                  {t("systemUsers.invite")}
                </Button>
              </Grid>
            </Grid>
          </Popper>
        </Grid>
      </form>
    </Grid>
  );
};

export default InviteAdmin;
